// JobPostingForm.jsx
import React, { useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Navbar from './Navbar';

function JobPostingForm() {
  const [salaryType, setSalaryType] = useState('');
  const [selectedEmploymentType, setSelectedEmploymentType] = useState('');
  const [skills, setSkills] = useState([]);
  const [formData, setFormData] = useState({ jobTitle: '', jobDescription: '' });
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [workType, setWorkType] = useState('');
  const [location, setLocation] = useState([]);

  const buttonStyles = (selected, type) =>
    `px-4 py-2 border cursor-pointer 
     ${selected === type ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border-gray-400'}
     rounded-tl-2xl rounded-br-2xl`;

  const locationsList = [
    { value: "Mumbai", label: "Mumbai" }, { value: "Delhi", label: "Delhi" }, { value: "Bangalore", label: "Bangalore" },
    { value: "Hyderabad", label: "Hyderabad" }, { value: "Pune", label: "Pune" }, { value: "Chennai", label: "Chennai" },
    { value: "Gurugram", label: "Gurugram" }, { value: "Noida", label: "Noida" }, { value: "Ahmedabad", label: "Ahmedabad" },
    { value: "Kolkata", label: "Kolkata" }, { value: "Surat", label: "Surat" }, { value: "Jaipur", label: "Jaipur" },
  ];

  const allSkills = [
    { value: 'Java', label: 'Java' }, { value: 'Python', label: 'Python' }, { value: 'JavaScript', label: 'JavaScript' },
    { value: 'React', label: 'React' }, { value: 'AWS', label: 'AWS' }, { value: 'Node.js', label: 'Node.js' },
    { value: 'SQL', label: 'SQL' }, { value: 'MongoDb', label: 'MongoDb' }, { value: 'Docker', label: 'Docker' },
  ];

  const handleLocationChange = (selectedOptions) => setLocation(selectedOptions || []);
  const handleSkillChange = (selectedOptions) => setSkills(selectedOptions || []);
  const handleQuillChange = (value) => setFormData({ ...formData, jobDescription: value });

  const generateDescription = () => {
    setLoading(true);
    setTimeout(() => {
      setFormData({ ...formData, jobDescription: 'Generated job description using AI...' });
      setLoading(false);
    }, 2000);
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile && (uploadedFile.type === 'application/pdf' ||
      uploadedFile.type === 'application/msword' ||
      uploadedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      setFile(uploadedFile);
    } else {
      alert('Please upload a valid .doc, .docx, or .pdf file.');
    }
  };

  return (
    <div className="w-full min-h-screen bg-white-500 overflow-hidden">
      <Navbar />
      <div className="flex-grow w-full mt-20 max-w-screen-xl mx-auto p-4 md:p-8 bg-gray-100 rounded-lg shadow-lg overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">New Job</h2>
        <hr className="mb-6" />

        <form className="space-y-8">
          {/* Basic Details */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-700">Basic Details</h3>
            <div className="py-3">
              <label className="block text-gray-700 font-medium mb-2">Upload JD (Optional)</label>
              <input
                type="file"
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleFileChange}
              />
            </div>

            <div className="pt-6 pb-3 flex justify-between items-center">
              <button
                type="submit"
                className="bg-blue-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-600"
              >
                Save
              </button>
              <button
                type="button"
                className="bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Job Title</label>
              <input
                type="text"
                placeholder="Enter Job Title"
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              />
              <p className="text-xs text-gray-400 mt-1">80 characters left. No special characters.</p>
            </div>
          </div>

          {/* Flexible Work */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Flexible Work</label>
            <div className="flex flex-wrap gap-4">
              {["In-Office", "Hybrid", "Remote"].map((type) => (
                <button
                  key={type}
                  onClick={() => setWorkType((prev) => (prev === type ? '' : type))}
                  className={buttonStyles(workType, type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Location</label>
            <CreatableSelect
              isMulti
              options={locationsList}
              value={location}
              onChange={handleLocationChange}
              placeholder="Type a location and select..."
              className="w-full"
            />
          </div>

          {/* Job Description */}
          <div className="relative mb-8">
            <label className="block text-gray-700 font-medium">Job Description</label>
            <ReactQuill
              value={formData.jobDescription}
              onChange={handleQuillChange}
              placeholder="Write job description here..."
              className="w-full h-64 sm:h-80 md:h-96 border border-gray-300 rounded-md"
              style={{ overflowY: 'auto' }}
            />
            <button
              onClick={generateDescription}
              className="absolute right-4 top-8 text-blue-600 font-semibold z-10 mr-10"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Write with AI'}
            </button>
          </div>

          {/* Skills */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Skills Required (min 3 Skills)</label>
            <CreatableSelect
              isMulti
              options={allSkills}
              value={skills}
              onChange={handleSkillChange}
              placeholder="Type a skill and select..."
              className="w-full"
            />
          </div>
          {/* Salary Range */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Salary Range</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="From"
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="To"
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select className="w-full border border-gray-300 rounded-md px-4 py-2">
                <option>INR</option>
                <option>USD</option>
              </select>
            </div>
          </div>

          {/* Employment Type */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Employment Type</label>
            <div className="flex flex-wrap gap-4">
              {['Full Time', 'Part Time', 'Contract', 'Internship'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedEmploymentType((prev) => (prev === type ? '' : type))}
                  className={buttonStyles(selectedEmploymentType, type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Education and Openings */}
          <div className="p-auto">
            <form className="flex gap-60">
              <div className="flex flex-col">
                <label htmlFor="education" className="block text-gray-700 font-semibold mb-2 pt-2">
                  Education (Optional)
                </label>
                <select
                  id="education"
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="" disabled selected>Select Education Level</option>
                  <option>High School</option>
                  <option>Diploma</option>
                  <option>Bachelor's</option>
                  <option>Master's</option>
                </select>
              </div>
            </form>
          </div>
        </form>
      </div>
    </div>
  );
}

export default JobPostingForm;